import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import ArrowBottomIcon from "../../Components/Icon-SVG/ArrowBottomIcon";
import ArrowRightIcon from "../../Components/Icon-SVG/ArrowRightIcon";
import DownloadIcon from "../../Components/Icon-SVG/DownloadIcon";
import SelectLogo from "../../Components/PagesComponents/ReportExport/SelectLogo/SelectLogo";
import Backdrop from "../../Components/UI/Backdrop/Backdrop";

import HostInformationCard from "../AnalysisReports/ThreatHunterAnalysisReports/components/InformationCards/HostInformationCard";
import OSInformationCard from "../AnalysisReports/ThreatHunterAnalysisReports/components/InformationCards/OSInformationCard";
import RiskScoreGauge from "../AnalysisReports/ThreatHunterAnalysisReports/components/RiskScoreGauge/RiskScoreGauge";
import ThreatSummary from "../AnalysisReports/ThreatHunterAnalysisReports/components/ThreatSummary/ThreatSummary";
import SecurityRatings from "../AnalysisReports/ThreatHunterAnalysisReports/components/SecurityRatings/SecurityRatings";
import ThreatCategoriesChart from "../AnalysisReports/ThreatHunterAnalysisReports/components/ThreatCategoriesChart/ThreatCategoriesChart";
import TopRiskyFiles from "../AnalysisReports/ThreatHunterAnalysisReports/components/TopRiskyFiles/TopRiskyFiles";
import TopRiskyProcesses from "../AnalysisReports/ThreatHunterAnalysisReports/components/TopRiskyProcesses/TopRiskyProcesses";
import SecurityIssues from "../AnalysisReports/ThreatHunterAnalysisReports/components/SecurityIssues/SecurityIssues";
import ThreatDetailsFlat from "../AnalysisReports/ThreatHunterAnalysisReports/components/ThreatDetailsBySeverity/ThreatDetailsFlat";
import EnrichedThreatDataTable from "../AnalysisReports/ThreatHunterAnalysisReports/components/EnrichedDataVisualization/EnrichedThreatDataTable";

import { RequestAgentAnalysisData } from "../../store/actions/integrationsActions";
import { getThreatHunterCsvData, getThreatHunterReportFields } from "./utils/dataSanitization";
import { THREAT_HUNTER_REPORT_LOGO } from "../../utils/constants/reportLogos";

import "./styles.scss";

//GET AGENT REPORT ID IN URL
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

const downloadFile = (content, type, fileName) => {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const ThreatHunterReportExport = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  let query = useQuery();

  const agentAnalysisData = useSelector((state) => state.integrations.agentAnalysisData);
  const agentAnalysisDataLoading = useSelector(
    (state) => state.integrations.agentAnalysisDataLoading,
  );

  const [systemTab, setSystemTab] = useState(true);
  const [riskTab, setRiskTab] = useState(true);
  const [categoriesTab, setCategoriesTab] = useState(true);
  const [filesTab, setFilesTab] = useState(true);
  const [processesTab, setProcessesTab] = useState(true);
  const [issuesTab, setIssuesTab] = useState(true);
  const [threatDetailsTab, setThreatDetailsTab] = useState(true);
  const [enrichedTab, setEnrichedTab] = useState(true);

  const originalID = query.get("data");

  useEffect(() => {
    dispatch(RequestAgentAnalysisData({ originalID }));
  }, [dispatch, originalID]);

  const hostName = agentAnalysisData?.raw_report?.systeminfo?.hostname || "Machine";

  const handleGeneratePdf = () => {
    history.push(
      `threat-hunter-report-pdf?data=${originalID}&s=${systemTab}&r=${riskTab}&c=${categoriesTab}&f=${filesTab}&p=${processesTab}&i=${issuesTab}&t=${threatDetailsTab}&e=${enrichedTab}`,
    );
  };

  const handleGenerateJson = () => {
    const jsonData = getThreatHunterReportFields(agentAnalysisData);

    downloadFile(
      JSON.stringify(jsonData, null, 2),
      "application/json",
      "Report - " + hostName + ".json",
    );
  };

  const handleGenerateCsv = () => {
    downloadFile(
      getThreatHunterCsvData(agentAnalysisData),
      "text/csv",
      "Report - " + hostName + ".csv",
    );
  };

  if (agentAnalysisDataLoading || !agentAnalysisData) {
    return <Backdrop show={true} absolute={true} message="Loading report..." />;
  }

  return (
    <div className="thunter-report">
      <div className="thunter-report-header">
        <h1>Report Generate</h1>
        <p>
          This section allows you to preview the report and export it in various formats. Please,
          collapse the sections not to view the details in the report.
        </p>
      </div>
      <div className="thunter-report-body">
        <div className="thunter-report-main">
          <div className="thunter-report-main__content">
            <div className="thunter-report-main__content__header">
              <SelectLogo defaultLogo={THREAT_HUNTER_REPORT_LOGO} />
              <div className="report-actions__buttons__container">
                <div
                  className="report-actions__buttons__container--export primary-button"
                  onClick={handleGeneratePdf}>
                  <div>Export to PDF</div>
                  <DownloadIcon color="var(--header-search-text-color)" width={16} height={16} />
                </div>
                <div
                  className="report-actions__buttons__container--export primary-button"
                  onClick={handleGenerateJson}>
                  Export to JSON
                  <DownloadIcon color="var(--header-search-text-color)" width={16} height={16} />
                </div>
                <div
                  className="report-actions__buttons__container--export primary-button"
                  onClick={handleGenerateCsv}>
                  Export to CSV
                  <DownloadIcon color="var(--header-search-text-color)" width={16} height={16} />
                </div>
              </div>
            </div>

            <div className="thunter-report-main__content--system">
              <h2 onClick={() => setSystemTab(!systemTab)}>
                System Information
                <span>
                  {systemTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`thunter-report-main__content--system-content ${
                  systemTab ? "visible" : "hidden"
                }`}>
                <HostInformationCard
                  clientID={agentAnalysisData?.clientID || ""}
                  hostData={agentAnalysisData?.raw_report?.systeminfo || {}}
                />
                <OSInformationCard
                  osData={agentAnalysisData?.raw_report?.systeminfo?.osstatus || {}}
                />
              </div>
            </div>

            <div className="thunter-report-main__content--risk">
              <h2 onClick={() => setRiskTab(!riskTab)}>
                Risk Overview
                <span>
                  {riskTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`thunter-report-main__content--risk-content ${
                  riskTab ? "visible" : "hidden"
                }`}>
                <SecurityRatings ratings={agentAnalysisData?.system_info?.security_ratings || {}} />
                <RiskScoreGauge riskScore={agentAnalysisData?.risk_score} />
                <ThreatSummary threatCounts={agentAnalysisData?.threat_counts} />
              </div>
            </div>

            <div className="thunter-report-main__content--categories">
              <h2 onClick={() => setCategoriesTab(!categoriesTab)}>
                Threat Categories
                <span>
                  {categoriesTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`thunter-report-main__content--categories-content ${
                  categoriesTab ? "visible" : "hidden"
                }`}>
                <ThreatCategoriesChart
                  threatCategories={agentAnalysisData?.top_threat_categories}
                />
              </div>
            </div>

            <div className="thunter-report-main__content--files">
              <h2 onClick={() => setFilesTab(!filesTab)}>
                Top Risky Files
                <span>
                  {filesTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`thunter-report-main__content--files-content ${
                  filesTab ? "visible" : "hidden"
                }`}>
                <TopRiskyFiles files={agentAnalysisData?.top_risky_files} />
              </div>
            </div>

            <div className="thunter-report-main__content--processes">
              <h2 onClick={() => setProcessesTab(!processesTab)}>
                Top Risky Memory Processes
                <span>
                  {processesTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`thunter-report-main__content--processes-content ${
                  processesTab ? "visible" : "hidden"
                }`}>
                <TopRiskyProcesses
                  processes={agentAnalysisData?.top_risky_memory_processes || []}
                />
              </div>
            </div>

            <div className="thunter-report-main__content--issues">
              <h2 onClick={() => setIssuesTab(!issuesTab)}>
                Security Issues
                <span>
                  {issuesTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`thunter-report-main__content--issues-content ${
                  issuesTab ? "visible" : "hidden"
                }`}>
                <SecurityIssues issues={agentAnalysisData?.security_issues} />
              </div>
            </div>

            <div className="thunter-report-main__content--threat-details">
              <h2 onClick={() => setThreatDetailsTab(!threatDetailsTab)}>
                Threat Details by Severity
                <span>
                  {threatDetailsTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`thunter-report-main__content--threat-details-content ${
                  threatDetailsTab ? "visible" : "hidden"
                }`}>
                <ThreatDetailsFlat data={agentAnalysisData?.threat_details_by_severity || {}} />
              </div>
            </div>

            <div className="thunter-report-main__content--enriched">
              <h2 onClick={() => setEnrichedTab(!enrichedTab)}>
                Enriched Threat Data
                <span>
                  {enrichedTab ? (
                    <ArrowBottomIcon width={16} height={16} />
                  ) : (
                    <ArrowRightIcon width={16} height={16} />
                  )}
                </span>
              </h2>
              <div
                className={`thunter-report-main__content--enriched-content ${
                  enrichedTab ? "visible" : "hidden"
                }`}>
                <EnrichedThreatDataTable
                  analysisDetails={agentAnalysisData?.analysis_details}
                  enrichedMaliciousHashes={agentAnalysisData?.enriched_malicious_hashes}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThreatHunterReportExport;
